import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Mail, CheckCircle, Eye, RefreshCw, Clock, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_INFO = {
  new:  { label: 'Nouveau',  icon: Clock,       cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  read: { label: 'Lu',       icon: Eye,         cls: 'bg-blue-100 text-blue-600 border-blue-300' },
  done: { label: 'Traité',   icon: CheckCircle, cls: 'bg-green-100 text-green-700 border-green-300' },
};

export const AdminContactPage = () => {
  const { user: me } = useAuth();
  const navigate     = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('all');

  useEffect(() => {
    if (me && me.role !== 'admin') { navigate('/dashboard'); return; }
    fetchRequests();
  }, [me]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/contact`, { withCredentials: true });
      setRequests(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API_URL}/api/contact/${id}`, { status }, { withCredentials: true });
      setRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    } catch (e) { console.error(e); }
  };

  const displayed = filter === 'all'
    ? requests
    : requests.filter(r => r.status === filter);

  const newCount = requests.filter(r => r.status === 'new').length;

  if (me?.role !== 'admin') return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-bold mb-1"
            style={{ color: 'var(--forest)', fontFamily: 'Cormorant Garamond, serif' }}>
            Demandes de contact
          </h1>
          <p style={{ color: 'var(--text-muted)' }}>
            {newCount > 0
              ? <span style={{ color: '#d97706', fontWeight: 600 }}>
                  {newCount} nouvelle{newCount > 1 ? 's' : ''} demande{newCount > 1 ? 's' : ''}
                </span>
              : 'Toutes les demandes sont traitées'}
          </p>
        </div>
        <button onClick={fetchRequests}
          className="flex items-center gap-2 px-3 py-2 text-sm border rounded-sm"
          style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)',
                   backgroundColor: 'var(--bg-card)' }}>
          <RefreshCw size={15} /> Actualiser
        </button>
      </div>

      {/* Filtres */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'all',  label: `Toutes (${requests.length})` },
          { key: 'new',  label: `Nouvelles (${requests.filter(r=>r.status==='new').length})` },
          { key: 'read', label: `Lues (${requests.filter(r=>r.status==='read').length})` },
          { key: 'done', label: `Traitées (${requests.filter(r=>r.status==='done').length})` },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setFilter(key)}
            className="px-3 py-1.5 text-sm border rounded-sm transition-colors"
            style={{
              backgroundColor: filter === key ? 'var(--forest)' : 'var(--bg-card)',
              color:           filter === key ? '#fff' : 'var(--text-secondary)',
              borderColor:     filter === key ? 'var(--forest)' : 'var(--border)',
            }}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>
          Chargement…
        </div>
      ) : displayed.length === 0 ? (
        <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>
          Aucune demande
        </div>
      ) : (
        <div className="space-y-3">
          {displayed.map(req => {
            const si     = STATUS_INFO[req.status] || STATUS_INFO.new;
            const Icon   = si.icon;
            const date   = new Date(req.created_at).toLocaleDateString('fr-FR', {
              day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            });

            return (
              <div key={req.id}
                className="border p-5"
                style={{
                  backgroundColor: req.status === 'new' ? 'rgba(217,119,6,0.04)' : 'var(--bg-card)',
                  borderColor: req.status === 'new' ? '#fcd34d' : 'var(--border)',
                }}>

                {/* En-tête */}
                <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Mail size={15} style={{ color: 'var(--forest)', flexShrink: 0 }} />
                      <span className="font-semibold text-sm"
                        style={{ color: 'var(--text-primary)' }}>
                        {req.subject}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 border rounded-full font-medium ${si.cls}`}>
                        <Icon size={10} /> {si.label}
                      </span>
                    </div>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                      <strong>{req.from_name}</strong> ({req.from_user}) · {date}
                      {req.person_ref && (
                        <> · Réf : <em>{req.person_ref}</em></>
                      )}
                    </p>
                  </div>

                  {/* Actions de statut */}
                  <div className="flex items-center gap-2 shrink-0">
                    {req.status === 'new' && (
                      <button onClick={() => updateStatus(req.id, 'read')}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs border rounded-sm"
                        style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)',
                                 backgroundColor: 'var(--bg-subtle)' }}>
                        <Eye size={12} /> Marquer lu
                      </button>
                    )}
                    {req.status !== 'done' && (
                      <button onClick={() => updateStatus(req.id, 'done')}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs border rounded-sm text-white"
                        style={{ backgroundColor: 'var(--forest)', borderColor: 'var(--forest)' }}>
                        <Check size={12} /> Traité
                      </button>
                    )}
                    {req.status === 'done' && (
                      <button onClick={() => updateStatus(req.id, 'new')}
                        className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        Rouvrir
                      </button>
                    )}
                  </div>
                </div>

                {/* Message */}
                <div className="text-sm border-l-2 pl-4 whitespace-pre-wrap"
                  style={{ borderColor: 'var(--forest)', color: 'var(--text-secondary)',
                           backgroundColor: 'var(--bg-subtle)', padding: '10px 14px',
                           borderRadius: '0 4px 4px 0' }}>
                  {req.message}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
